import React, {useState, useContext, useEffect} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import "./TopicContentViewer.css";
import {Button, IconButton, LoadingSkeleton} from "@spg/component_library";
import {SnackbarContext} from "../../../reusables/SnackbarProvider";
import API, {Resolution} from "../../../logic/API";
import {
  DatasetDescription,
  DisplayableDataset,
  DownloadableDataset,
  FileUploadDataset,
  TopicDescriptionInfo,
} from "../../../logic/APITypes";
import chevronIcon from "../../../images/chevron.svg";
import searchIcon from "../../../images/search.svg";
import {useNavigate} from "react-router-dom";
import {supportedDisplayableDatasetTypes} from "../../../logic/structuredText/Formatting";
import {formatFileSize} from "../../../logic/Units";
import {TopicContentViewerSelection} from "./TopicContentViewerSelection";
import {TopicContentViewerInfoBlock} from "./TopicContentViewerInfoBlock";
import {TopicContentViewerContent} from "./TopicContentViewerContent";
import {TopicContentViewerSearch} from "./TopicContentViewerSearch";

/**
 * A page displaying recent datasets received by a topic.
 */
function TopicContentViewer(props: {topicList?: TopicDescriptionInfo[]}) {
  const addSnackbar = useContext(SnackbarContext).addSnackbar;
  const intl = useIntl();
  const navigate = useNavigate();

  const [dataset, setDataset] = useState<Resolution<DatasetDescription>>({
    unresolved: true,
    success: false,
  });
  const [datasetIndex, setDatasetIndex] = useState(0);
  const [shouldFormat, setShouldFormat] = useState(false);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [loadingTopic, setLoadingTopic] = useState("");

  const currentDataset:
    | undefined
    | ({type: "file"} & FileUploadDataset)
    | ({type: "display"} & DisplayableDataset)
    | ({type: "download"} & DownloadableDataset) =
    !dataset.payload || !dataset.payload.datasets[datasetIndex]
      ? undefined
      : dataset.payload.transfer_type === "FILE_UPLOAD"
        ? {
            type: "file",
            ...(dataset.payload.datasets[datasetIndex] as FileUploadDataset),
          }
        : (supportedDisplayableDatasetTypes as readonly string[]).includes(
              dataset.payload?.datasets[datasetIndex].mime_type || "",
            )
          ? {
              type: "display",
              ...(dataset.payload.datasets[datasetIndex] as DisplayableDataset),
            }
          : {
              type: "download",
              ...(dataset.payload.datasets[
                datasetIndex
              ] as DownloadableDataset),
            };

  const note =
    (currentDataset?.mime_type === "text/csv" && shouldFormat) ||
    currentDataset?.type === "file"
      ? [
          intl.formatMessage({
            id:
              "topicContentViewer.infoBlock.value.note." +
              (currentDataset?.type === "file" ? "fileUpload" : "csv"),
          }),
        ]
      : [];

  const topicId = (location.pathname.match(/datasets\/([0-9a-f-]*)/) || [
    "",
    "",
  ])[1];

  const reloadButtonProps: React.ComponentPropsWithoutRef<typeof Button> = {
    label: intl.formatMessage({
      id: "topicContentViewer.controls.reload",
    }),
    idBase: "topicContentViewerReload",
    onClick: () => {
      setWaitingForResponse(true);
      API.getDatasetDescription(topicId).then((response) => {
        setWaitingForResponse(false);
        if (response.payload) {
          setDataset(response);
          setDatasetIndex(0);
        } else
          addSnackbar({
            variant: "close",
            hasCloseButton: true,
            text: intl.formatMessage({
              id: "topicContentViewer.snackbar.reload.failed",
            }),
          });
      });
    },
    configuration: "primary",
    isWide: true,
    isDisabled: waitingForResponse || !dataset.payload,
  };

  useEffect(() => {
    setWaitingForResponse(true);
    API.getDatasetDescription(topicId).then((response) => {
      setDataset(response);
      setWaitingForResponse(false);
    });
  }, [topicId]);

  return (
    <div className={"topicContentViewerContainer landingPageGrid"}>
      <h1 id="topicContentViewerTitle">
        {
          <>
            <IconButton
              idBase={"topicContentViewerBack"}
              onClick={() => navigate("/topics/overview/" + topicId)}
              iconProps={{
                href: chevronIcon + "#chevronLeft",
                title: intl.formatMessage({
                  id: "topicContentViewer.back",
                }),
                strokeColor: "black",
              }}
              hoverColor="#ff3d00"
              buttonSize={{height: 36, width: 36}}
            />
            <span className="topicContentViewerTitle">
              <FormattedMessage
                id="topicContentViewer.title"
                values={{
                  b: (chunks) => <b>{chunks}</b>,
                }}
              />
            </span>
          </>
        }
      </h1>
      {dataset.unresolved || dataset.payload ? ( // renders loading-animation/content vs an error message
        <>
          {!dataset.payload ? (
            <LoadingSkeleton />
          ) : props.topicList && searchOpen ? (
            <TopicContentViewerSearch
              topicList={props.topicList}
              currentId={topicId}
              disabled={waitingForResponse}
              closeMenu={() => setSearchOpen(false)}
              setLoadingTopic={setLoadingTopic}
            />
          ) : (
            <div className="topicContentViewerName">
              <div>{loadingTopic || dataset.payload.topic_name}</div>
              <IconButton
                iconProps={{
                  href: searchIcon + "#globalG",
                  title: intl.formatMessage({
                    id: "topicContentViewer.search",
                  }),
                  fillColor: "black",
                  iconSize: {width: 24, height: 24},
                }}
                idBase={"topicContentViewerSearch"}
                onClick={() => setSearchOpen(true)}
                hoverColor="#ff3d00"
                disabled={waitingForResponse}
              />
            </div>
          )}
          <div className={"topicContentViewerContent"}>
            {dataset.payload && currentDataset && !waitingForResponse ? ( // renders content vs a loading-skeleton
              <TopicContentViewerContent
                currentDataset={currentDataset}
                topicName={dataset.payload.topic_name}
                selection={
                  dataset.payload.datasets.length > 1 ? (
                    <TopicContentViewerSelection
                      datasetIndex={datasetIndex}
                      setDatasetIndex={setDatasetIndex}
                      datasetCount={dataset.payload.datasets.length}
                    />
                  ) : undefined
                }
                shouldFormat={{get: shouldFormat, set: setShouldFormat}}
                reloadButtonProps={reloadButtonProps}
              />
            ) : dataset.payload && !waitingForResponse ? (
              <div className="topicContentViewerBlankText">
                <FormattedMessage
                  id={
                    "topicContentViewer.noDatasets." +
                    dataset.payload.transfer_direction.toLowerCase()
                  }
                />
                <Button {...reloadButtonProps} />
              </div>
            ) : (
              <LoadingSkeleton height="400px" />
            )}
          </div>
          <TopicContentViewerInfoBlock
            title="general"
            keys={[
              "createdAt",
              "owner",
              "status",
              "modifiedAt",
              "transferType",
              "visibility",
            ]}
            values={
              dataset.payload && !waitingForResponse
                ? [
                    intl.formatDate(dataset.payload.created_at, {
                      dateStyle: "short",
                    }),
                    dataset.payload.owner_name,
                    intl.formatMessage({
                      id:
                        "topicDescriptionConnector.health." +
                        dataset.payload.health,
                    }),
                    intl.formatDate(dataset.payload.modified_at, {
                      timeStyle: "medium",
                      dateStyle: "short",
                    }),
                    intl.formatMessage({
                      id:
                        "topicDescriptionConnector.method." +
                        (dataset.payload.transfer_type === "REST"
                          ? "rest_" +
                            dataset.payload.transfer_direction.toLowerCase()
                          : dataset.payload.transfer_type.toLowerCase()),
                    }),
                    intl.formatMessage({
                      id:
                        "topicDescriptionCreation.inputLabel.visibility." +
                        dataset.payload.visibility.toLowerCase(),
                    }),
                  ]
                : undefined
            }
          />
          {currentDataset && (
            <TopicContentViewerInfoBlock
              title="dataset"
              keys={[
                "count",
                "size",
                "receivedAt",
                "mime_type",
                ...(note.length === 0 ? [] : ["note"]),
              ]}
              values={
                dataset.payload && currentDataset && !waitingForResponse
                  ? [
                      "" + dataset.payload.datasets_count,
                      formatFileSize(currentDataset.size, intl),
                      intl.formatDate(currentDataset.received_at, {
                        timeStyle: "medium",
                        dateStyle: "short",
                      }),
                      currentDataset.type === "download"
                        ? intl.formatMessage({
                            id: "topicContentViewer.infoBlock.value.mime_type.unknown",
                          })
                        : currentDataset.mime_type,
                      ...note,
                    ]
                  : undefined
              }
            />
          )}
        </>
      ) : (
        <div className="topicContentViewerBlankText">
          <FormattedMessage id="topicContentViewer.failedToLoad" />
        </div>
      )}
    </div>
  );
}

export {TopicContentViewer};
